import React, { useEffect, useState } from "react";

/**
 * Provisioning banner — shows on top of the app for the ~30-60s window
 * where a fresh account's Stripe→Clerk tier sync is still in flight.
 *
 * Auto-polls `clerkUser.reload()` every 8s; unmounts itself as soon as
 * publicMetadata.tier appears. Hard-stops polling after 3 min so a
 * genuinely stuck sync doesn't burn cycles forever.
 */
const TierProvisioningBanner = ({ clerkUser }) => {
  const [attempts, setAttempts] = useState(0);
  const HARD_STOP = 22; // ~3 min at 8s cadence

  useEffect(() => {
    if (!clerkUser?.reload) return;
    if (attempts >= HARD_STOP) return;
    const t = setTimeout(async () => {
      try { await clerkUser.reload(); } catch (_) { /* transient — try again */ }
      setAttempts((n) => n + 1);
    }, 8000);
    return () => clearTimeout(t);
  }, [attempts, clerkUser]);

  const stuck = attempts >= HARD_STOP;

  return (
    <div
      className="hf-reveal"
      style={{
        background: stuck ? "var(--bg-warning-subtle)" : "var(--bg-accent-subtle)",
        borderBottom: "1px solid var(--hf-border, rgba(255, 255, 255, 0.09))",
        boxShadow: "var(--hf-shadow-1, 0 2px 10px rgba(0, 0, 0, 0.25))",
        color: "var(--text-primary)",
        padding: "10px 20px",
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
      }}
    >
      <span style={{ fontSize: 16 }}>{stuck ? "⚠️" : "✨"}</span>
      <span>
        {stuck
          ? "Still finalizing your account. If this persists, refresh or reach us at support@osoptimus.com."
          : "Finalizing your account — this usually clears in a few seconds."}
      </span>
    </div>
  );
};

export default TierProvisioningBanner;
